package frontend.models

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.ionspin.kotlin.bignum.decimal.BigDecimal
import kotlinx.serialization.Serializable
import net.sergeych.bintools.KVStorage
import net.sergeych.kilogin.biDecode
import net.sergeych.kilogin.biEncode
import net.sergeych.ugdx.GuestAPI
import net.sergeych.ugdx.UserAccount
import net.sergeych.ugdx.shared.Currency
import net.sergeych.ugdx.shared.Order
import net.sergeych.ugdx.shared.api.ApiAccount

val ADDRESSES_KEY = "addresses"

@Serializable
data class Address(val name: String, val address: String)

class User(userAcc: UserAccount) {
    var userAccount by mutableStateOf(userAcc)
    var accounts by mutableStateOf<List<ApiAccount>>(emptyList())
    var addresses = mutableStateListOf<Address>()
    val loginName = userAccount.loginName
    val ethereum = userAccount.ethereum

    suspend fun loadAccounts() {
        accounts = userAccount.accounts().accounts
    }

    /**
     * Create new account in a given currency. if [isDefault] is true, it will find first account in a given
     * currency or create new one; this mode is used to not create multiple accounts for the same currency
     * unless needed.
     */
    suspend fun createAccount(currency: Currency, name: String = "",isDefault: Boolean = true): ApiAccount {
        if( isDefault ) loadAccounts()
        return accounts.find { it.currencyCode == currency.code } ?: run {
            val account = userAccount.createAccount(currency, name)
            accounts += account
            return account
        }
    }

    fun updateAccount(new: ApiAccount) {
        val mutableList = accounts.toMutableList()
        val old = mutableList.find { it.id == new.id }
        old?.let {
            mutableList.set(mutableList.indexOf(it), new)
            accounts = mutableList
        }
    }

    fun updateAccount(old: ApiAccount, new: ApiAccount) {
        val mutableList = accounts.toMutableList()
        mutableList.set(mutableList.indexOf(old), new)
        accounts = mutableList
    }

    suspend fun refreshAccount(account: ApiAccount) {
        val updated = account.refresh()
        val mutableList = accounts.toMutableList()
        mutableList.set(mutableList.indexOf(account), updated)
        accounts = mutableList
    }

    suspend fun deleteAccount(account: ApiAccount) {
        userAccount.deleteAccount(account)
        accounts -= account
    }

    suspend fun transferLocal(from: ApiAccount, to: ApiAccount, amount: BigDecimal) {
        userAccount.transferToMyAccount(from, to, amount)
    }

    fun createTransferOrder(
        from: ApiAccount,
        amount: BigDecimal,
        toLoginName: String,
        purpose: String
    ): Order {
        return userAccount.createTransferOrder(from, amount, toLoginName, purpose)
    }

    suspend fun addAddress(address: Address) {
        var newAddresses: List<Address> = userAccount.getDataList(ADDRESSES_KEY)
        if (newAddresses.find { it.address == address.address } != null)
            throw Exception("Address already attached")
        newAddresses += address
        userAccount.setDataList(ADDRESSES_KEY, newAddresses)
        GuestAPI().storage.write(ADDRESSES_KEY, newAddresses)
        addresses.clear()
        addresses.addAll(newAddresses)
    }

    fun getAddress(address: String): Address? {
        return addresses.find { it.address == address }
    }

    suspend fun deleteAddress(address: Address) {
        var newAddresses: List<Address> = userAccount.getDataList(ADDRESSES_KEY)
        newAddresses -= address
        userAccount.setDataList(ADDRESSES_KEY, newAddresses)
        GuestAPI().storage.write(ADDRESSES_KEY, newAddresses)
        addresses.clear()
        addresses.addAll(newAddresses)
    }

    suspend fun load() {
        loadAccounts()
        loadAddresses()
//        loadSettings()
    }

    suspend fun loadAddresses(isEncrypted: Boolean = false) {
        if (!isEncrypted && !userAccount.hasDecryptedKeys()) {
            val addressesList: List<Address> = GuestAPI().storage.read(ADDRESSES_KEY) ?: emptyList()
            addresses.clear()
            addresses.addAll(addressesList)
        }
        else {
            val addressesList: List<Address> = userAccount.getDataList(ADDRESSES_KEY)
            GuestAPI().storage.write(ADDRESSES_KEY, addressesList)
            addresses.clear()
            addresses.addAll(addressesList)
        }
    }
}

inline fun <reified T>KVStorage.write(key: String,value: T) {
    set(key, biEncode<T>(value).asByteArray())
}

inline fun <reified T>KVStorage.read(key: String): T? = get(key)?.let {biDecode<T>(it) }



